import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AboutImg from "../../assets/images/aboutnew.png";
import { useNavigate } from "react-router-dom";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import {
  getUserProfile,
  UserProfile,
} from "../../API/UserProfileApi/GetUserProfile";
import { getAboutContent } from "../../API/About/GetAbout";
import ReactGA from "react-ga4";
import { useLocalization } from "../Context/MultiLanguageContext";
import packageJson from "../../../package.json";
import {
  getApiBaseUrl,
  PREPROD_API_BASE_URL,
} from "../../utils/apiBaseUrl";

const About: React.FC<{
  updateUserName: (newUserName: string) => void;
}> = ({ updateUserName }) => {
  const navigate = useNavigate();
  const { t } = useLocalization();

  const [aboutContent, setAboutContent] = useState("");
  const [aboutHeading, setAboutHeading] = useState("");
  const [userName, setUserName] = useState("");
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const isPreProd = getApiBaseUrl() === PREPROD_API_BASE_URL;

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const {
          aboutContent: content,
          aboutHeading: heading,
        } = await getAboutContent();

        setAboutContent(content);
        setAboutHeading(heading);
      } catch (error) {
        toast.error(t("fetchAboutContent"));
      }
    };

    fetchContent();
  }, [t]);

  useEffect(() => {
    const storedUserName = localStorage.getItem("userName");
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  const fetchAndHandleUserProfile = async () => {
    try {
      const data = await getUserProfile(t);

      setUserProfile(data);
      setUserName(data.userName);
      updateUserName(data.userName);

      ReactGA.event({
        category: "User",
        action: "Fetched Profile",
        label: data.userName,
      });

      navigate("/profile", {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      toast.error(t("sessionExpired"));
      navigate("/home");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("authToken");
    localStorage.removeItem("token");
    localStorage.removeItem("userName");

    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-magnolia">
      <div className="flex flex-col min-h-screen">
        <Header
          userName={userName}
          fetchUserProfile={fetchAndHandleUserProfile}
          handleLogout={handleLogout}
        />

        <main className="flex-1 overflow-auto">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-dark text-center">
              {t("about")}
            </h4>

            <div className="w-full mt-6">
              <div className="w-full bg-white rounded-lg shadow-md p-4 sm:p-6 md:p-8">
                <div className="text-center">
                  <img
                    className="inline-block mx-auto w-1/2 max-w-48 sm:w-1/3 sm:max-w-48 md:w-1/3 md:max-w-48 lg:w-1/3 lg:max-w-48"
                    src={AboutImg}
                    alt="About PAM"
                  />

                  <h4 className="mt-6 mb-4 text-lg sm:text-lg md:text-lg font-semibold text-dark">
                    {aboutHeading || "Postal Address Map (PAM)"}
                  </h4>

                  {aboutContent ? (
                    <div
                      className="w-full lg:w-3/4 mx-auto text-sm sm:text-sm text-jetgrey break-words"
                      dangerouslySetInnerHTML={{
                        __html: aboutContent,
                      }}
                    />
                  ) : (
                    <div className="text-sm sm:text-base text-gray-500">
                      Loading...
                    </div>
                  )}

                  <div className="mt-8">
                    <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-6 text-sm">
                      <a
                        href="https://getmethere.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        https://getmethere.in
                      </a>

                      <a
                        href="https://vofoxsolutions.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        https://vofoxsolutions.com
                      </a>

                      <span className="text-center break-words">
                        PAM Code :{" "}
                        <span className="font-bold">
                          682017-3715-1
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-4 sm:mx-6 lg:mx-8 mb-4 p-2 text-center bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600">
              {isPreProd ? (
                <span>
                  Version {packageJson.version} [Beta] Pre-Prod
                </span>
              ) : (
                <span>
                  Version {packageJson.version} [Beta]
                </span>
              )}
            </p>
          </div>
        </main>

        <Footer />
      </div>

      <ToastContainer />
    </div>
  );
};

export default About;